
import { useState, useEffect, useCallback } from 'react';
import { Customer } from '@/lib/supabase/customers/types/CustomerTypes';
import { CustomerStatus } from '@/lib/supabase/customers/types/CustomerTypes';
import { useToast } from '@/components/ui/use-toast';
import { getAllCustomersByOrganization } from '@/lib/supabase/customers/queries/getCustomers';

interface UseCustomerFetchingProps {
  shopId?: string;
  status?: CustomerStatus;
}

export const useCustomerFetching = ({ shopId, status = 'all' }: UseCustomerFetchingProps = {}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string>('');
  const { toast } = useToast();

  // Récupération de l'ID d'organisation
  useEffect(() => {
    const orgId = localStorage.getItem('currentOrganizationId');
    if (orgId) {
      setOrganizationId(orgId);
    } else {
      console.error('[useCustomerFetching] No organization ID found in localStorage');
      toast({
        title: 'Erreur',
        description: "Impossible de charger les clients sans identifiant d'organisation",
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Fonction pour charger les clients
  const fetchCustomers = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
      console.log('[useCustomerFetching] Fetching customers with organizationId:', organizationId, 'status:', status, 'shopId:', shopId);
      const fetchedCustomers = await getAllCustomersByOrganization(organizationId, status, shopId);
      console.log('[useCustomerFetching] Fetched customers count:', fetchedCustomers.length);
      
      setCustomers(fetchedCustomers);
      setFilteredCustomers(fetchedCustomers);
    } catch (error) {
      console.error('[useCustomerFetching] Error fetching customers:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les clients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, status, shopId, toast]);

  // Chargement initial des clients
  useEffect(() => {
    if (organizationId) {
      console.log('[useCustomerFetching] Loading customers for organization:', organizationId);
      fetchCustomers();
    }
  }, [organizationId, fetchCustomers]);

  // Rechargement des clients quand le statut change
  useEffect(() => {
    if (organizationId) {
      console.log('[useCustomerFetching] Status changed to:', status, 'reloading customers');
      fetchCustomers();
    }
  }, [status, fetchCustomers]);

  return {
    customers,
    filteredCustomers,
    loading,
    fetchCustomers,
    organizationId
  };
};
